import React, { useEffect } from "react";

import { useSelector } from "react-redux";
import { useSnapshot } from "valtio";

import Heading from "@busbud/design-system-components/dist/Heading";
import IconButton from "@busbud/design-system-components/dist/IconButton";
import CrossMD from "@busbud/design-system-components/dist/Icons/Cross/MD";
import Stack from "@busbud/design-system-components/dist/Stack";

import { Bus } from "@app/components/bus/bus";
import FloorSelector from "@app/components/bus/floor-selector/floor-selector";
import { useAppContext, useAppDispatch } from "@app/helpers/hooks";
import { closeDrawer, updateSearch } from "@app/modules/results/actions";
import { LoadingBusLayout } from "@app/modules/results/components/departure/seat-selection-details/loading-bus-layout";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { seatClassForTrips } from "@app/modules/seat-selection-first/helpers/seat-class-for-trips";
import { useAutoSelectInitialFloor } from "@app/modules/seat-selection-first/hooks/use-auto-select-floor";
import { useFetchSegmentVehicleIfNotLoaded } from "@app/modules/seat-selection-first/hooks/use-fetch-segment-vehicle";
import { useInitializeSeatSelectionStore } from "@app/modules/seat-selection-first/hooks/use-initialize-seat-selection-store";
import {
  changeFloor,
  getPassengersCount,
  getSegmentSelectedSeats,
  removePreviousSegmentSeats,
  toggleSeatSelection,
  valtio_store
} from "@app/modules/seat-selection-first/store";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import type { ConnectorProps } from "./seat-selection.connect";

import { SeatSelectionBottomSection } from "./seat-selection-bottom-section";
import { useStyles } from "./seat-selection.styles";

export const SeatSelection: React.FC<ConnectorProps> = ({
  is_external,
  is_vehicle_not_found,
  next_segment_id,
  onClose,
  segment_id,
  trip_id,
  trip_option,
  trip_options_matrix,
  trip_segment_ids,
  vehicle_layouts,
  vehicle
}) => {
  const { translator, features } = useAppContext();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { active_floor } = useSnapshot(valtio_store);
  const seats_for_segment = getSegmentSelectedSeats(segment_id);
  const state = useSelector((state: ResultsReduxState) => state);
  const { goBack } = useNavigation();

  useFetchSegmentVehicleIfNotLoaded(
    trip_options_matrix.flat().map(({ trip_id }) => trip_id)
  );
  useInitializeSeatSelectionStore(trip_id, vehicle_layouts);
  useAutoSelectInitialFloor(vehicle);

  useEffect(() => {
    removePreviousSegmentSeats(trip_segment_ids);
  }, [trip_segment_ids]);

  useEffect(() => {
    if (is_vehicle_not_found) {
      dispatch(closeDrawer());
      goBack(translator.t("!error.CartDepartureExpired.text"));
    }
  }, [is_vehicle_not_found, goBack, translator, dispatch]);

  const seatClassResolver = (seat_id: string) => {
    return features.SEAT_SELECTION_DISPLAY_MULTIPLE_CLASSES
      ? seatClassForTrips(state, trip_options_matrix, seat_id)
      : null;
  };

  const is_loading = !trip_id || !vehicle?.layout;
  const floors_count = vehicle?.layout?.length || 0;

  return (
    <Stack className={classes.root} direction="column">
      <Stack className={classes.main} direction="column">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          className={classes.header}
          spacing={2}
        >
          <Heading component="h2" size="sm">
            {translator.t("!results.seat-selection.seat-selection")}
          </Heading>
          <IconButton
            onClick={onClose}
            color="tertiary"
            variant="text"
            size="sm"
          >
            <CrossMD />
          </IconButton>
        </Stack>

        {is_loading ? (
          <LoadingBusLayout class_name={classes.content} />
        ) : (
          <Stack direction="column" className={classes.content}>
            {floors_count > 1 && (
              <FloorSelector
                className={classes.floorSelector}
                tab_size="md"
                active_floor={active_floor}
                handleChange={changeFloor}
              />
            )}
            <div className={classes.busContainer}>
              <Bus
                orientation="top"
                key={`seat-selection-${trip_id}`}
                vehicle_layout={vehicle.layout}
                vehicle_driver={vehicle.driver}
                already_assigned_seat_ids={seats_for_segment.map(s => s.id)}
                active_seat_id=""
                onSeatChange={s => {
                  toggleSeatSelection(s, segment_id!);
                  // TODO-SSF: Instead of update next_segment_ide query parameters used in the search, let's add extra query parameters with the new desired number of adults and take this in the Consideration for the async-cart creation
                  dispatch(
                    updateSearch({
                      adult: getPassengersCount()
                    })
                  );
                }}
                is_checkout={false}
                active_floor={active_floor}
                seatClassResolver={seat_id => seatClassResolver(seat_id)}
              />
            </div>
          </Stack>
        )}
      </Stack>
      <div className={classes.footer}>
        <SeatSelectionBottomSection
          is_loading={is_loading}
          selected_trip={trip_option}
          is_external={is_external}
          segment_id={segment_id}
          next_segment_id={next_segment_id}
          trip_options_matrix={trip_options_matrix}
        />
      </div>
    </Stack>
  );
};
